import { useEffect, useMemo, useState } from 'react'
import { apiGet, apiPost, apiPut } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'
import { useToasts } from './Toasts'
import { usePerms } from '../auth/PermsContext'
import { useI18n } from '../i18n'
import Confirm from './parts/Confirm'

type Repo = {
  id: number
  name: string
  type: string
  config?: Record<string, any>
  active: boolean
  quarantined: boolean
  last_error?: string | null
  last_error_at?: string | null
  last_preflight_at?: string | null
  last_preflight_status?: string | null
  preflight_report?: Record<string, any> | null
  preflight_failure_streak?: number | null
}

type FieldDef = {
  name: string
  labelKey: string
  placeholderKey?: string
  helperKey?: string
  required?: boolean
  secret?: boolean
  type?: 'text' | 'textarea'
}

type RepoTypeMeta = {
  labelKey: string
  descriptionKey: string
  fields: FieldDef[]
}

const REPO_TYPES: Record<string, RepoTypeMeta> = {
  s3: {
    labelKey: 'repo.type.s3',
    descriptionKey: 'repo.type.s3.desc',
    fields: [
      { name: 'bucket', labelKey: 'repo.fields.bucket', placeholderKey: 'repo.placeholder.bucket', required: true },
      { name: 'region', labelKey: 'repo.fields.region', placeholderKey: 'repo.placeholder.region', required: true },
      { name: 'endpoint', labelKey: 'repo.fields.endpoint', placeholderKey: 'repo.placeholder.endpoint' },
      { name: 'access_key', labelKey: 'repo.fields.access_key', placeholderKey: 'repo.placeholder.access_key', required: true, secret: true },
      { name: 'secret_key', labelKey: 'repo.fields.secret_key', placeholderKey: 'repo.placeholder.secret_key', required: true, secret: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
      { name: 'prefix', labelKey: 'repo.fields.prefix', placeholderKey: 'repo.placeholder.prefix' },
      { name: 'storage_class', labelKey: 'repo.fields.storage_class', placeholderKey: 'repo.placeholder.storage_class' },
      { name: 'kms_key_id', labelKey: 'repo.fields.kms_key_id', placeholderKey: 'repo.placeholder.kms_key_id' },
    ],
  },
  local: {
    labelKey: 'repo.type.local',
    descriptionKey: 'repo.type.local.desc',
    fields: [
      { name: 'path', labelKey: 'repo.fields.path', placeholderKey: 'repo.placeholder.path', required: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
      { name: 'snapshot_retention', labelKey: 'repo.fields.snapshot_retention', placeholderKey: 'repo.placeholder.snapshot_retention' },
    ],
  },
  b2: {
    labelKey: 'repo.type.b2',
    descriptionKey: 'repo.type.b2.desc',
    fields: [
      { name: 'bucket', labelKey: 'repo.fields.bucket', placeholderKey: 'repo.placeholder.bucket', required: true },
      { name: 'endpoint', labelKey: 'repo.fields.endpoint', placeholderKey: 'repo.placeholder.b2_endpoint' },
      { name: 'key_id', labelKey: 'repo.fields.key_id', placeholderKey: 'repo.placeholder.key_id', required: true, secret: true },
      { name: 'application_key', labelKey: 'repo.fields.application_key', placeholderKey: 'repo.placeholder.application_key', required: true, secret: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
  azure: {
    labelKey: 'repo.type.azure',
    descriptionKey: 'repo.type.azure.desc',
    fields: [
      { name: 'account_name', labelKey: 'repo.fields.account_name', placeholderKey: 'repo.placeholder.account_name', required: true },
      { name: 'container', labelKey: 'repo.fields.container', placeholderKey: 'repo.placeholder.container', required: true },
      { name: 'endpoint', labelKey: 'repo.fields.endpoint', placeholderKey: 'repo.placeholder.azure_endpoint' },
      { name: 'account_key', labelKey: 'repo.fields.account_key', placeholderKey: 'repo.placeholder.account_key', required: true, secret: true },
      { name: 'sas_token', labelKey: 'repo.fields.sas_token', placeholderKey: 'repo.placeholder.sas_token', secret: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
  sftp: {
    labelKey: 'repo.type.sftp',
    descriptionKey: 'repo.type.sftp.desc',
    fields: [
      { name: 'host', labelKey: 'repo.fields.host', placeholderKey: 'repo.placeholder.host', required: true },
      { name: 'port', labelKey: 'repo.fields.port', placeholderKey: 'repo.placeholder.port' },
      { name: 'user', labelKey: 'repo.fields.user', placeholderKey: 'repo.placeholder.user', required: true },
      { name: 'path', labelKey: 'repo.fields.remote_path', placeholderKey: 'repo.placeholder.remote_path', required: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
      { name: 'ssh_private_key', labelKey: 'repo.fields.private_key', placeholderKey: 'repo.placeholder.private_key', helperKey: 'repo.helper.private_key', type: 'textarea', secret: true },
      { name: 'ssh_known_hosts', labelKey: 'repo.fields.known_hosts', placeholderKey: 'repo.placeholder.known_hosts', helperKey: 'repo.helper.known_hosts', type: 'textarea' },
      { name: 'ssh_options', labelKey: 'repo.fields.ssh_options', placeholderKey: 'repo.placeholder.ssh_options', helperKey: 'repo.helper.ssh_options' },
    ],
  },
  rest: {
    labelKey: 'repo.type.rest',
    descriptionKey: 'repo.type.rest.desc',
    fields: [
      { name: 'url', labelKey: 'repo.fields.url', placeholderKey: 'repo.placeholder.url', required: true },
      { name: 'username', labelKey: 'repo.fields.username', placeholderKey: 'repo.placeholder.username' },
      { name: 'http_password', labelKey: 'repo.fields.http_password', placeholderKey: 'repo.placeholder.http_password', secret: true },
      { name: 'auth_token', labelKey: 'repo.fields.auth_token', placeholderKey: 'repo.placeholder.auth_token', helperKey: 'repo.helper.auth_token', secret: true },
      { name: 'ca_cert', labelKey: 'repo.fields.ca_cert', placeholderKey: 'repo.placeholder.ca_cert', helperKey: 'repo.helper.ca_cert', type: 'textarea' },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
  swift: {
    labelKey: 'repo.type.swift',
    descriptionKey: 'repo.type.swift.desc',
    fields: [
      { name: 'auth_url', labelKey: 'repo.fields.auth_url', placeholderKey: 'repo.placeholder.auth_url', required: true },
      { name: 'username', labelKey: 'repo.fields.username', placeholderKey: 'repo.placeholder.username', required: true },
      { name: 'auth_password', labelKey: 'repo.fields.auth_password', placeholderKey: 'repo.placeholder.auth_password', required: true, secret: true },
      { name: 'project', labelKey: 'repo.fields.project', placeholderKey: 'repo.placeholder.project', required: true },
      { name: 'container', labelKey: 'repo.fields.container', placeholderKey: 'repo.placeholder.container', required: true },
      { name: 'region', labelKey: 'repo.fields.region', placeholderKey: 'repo.placeholder.region' },
      { name: 'domain', labelKey: 'repo.fields.domain', placeholderKey: 'repo.placeholder.domain' },
      { name: 'tenant_id', labelKey: 'repo.fields.tenant_id', placeholderKey: 'repo.placeholder.tenant_id' },
      { name: 'prefix', labelKey: 'repo.fields.prefix', placeholderKey: 'repo.placeholder.prefix' },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
  gs: {
    labelKey: 'repo.type.gs',
    descriptionKey: 'repo.type.gs.desc',
    fields: [
      { name: 'bucket', labelKey: 'repo.fields.bucket', placeholderKey: 'repo.placeholder.bucket', required: true },
      { name: 'prefix', labelKey: 'repo.fields.prefix', placeholderKey: 'repo.placeholder.prefix' },
      { name: 'project_id', labelKey: 'repo.fields.project_id', placeholderKey: 'repo.placeholder.project_id' },
      { name: 'credentials_json', labelKey: 'repo.fields.credentials_json', placeholderKey: 'repo.placeholder.credentials_json', helperKey: 'repo.helper.credentials_json', type: 'textarea', secret: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
  rclone: {
    labelKey: 'repo.type.rclone',
    descriptionKey: 'repo.type.rclone.desc',
    fields: [
      { name: 'remote', labelKey: 'repo.fields.remote', placeholderKey: 'repo.placeholder.remote', required: true },
      { name: 'path', labelKey: 'repo.fields.remote_path', placeholderKey: 'repo.placeholder.remote_path', required: true },
      { name: 'config_text', labelKey: 'repo.fields.config_text', placeholderKey: 'repo.placeholder.config_text', helperKey: 'repo.helper.config_text', type: 'textarea', secret: true },
      { name: 'password', labelKey: 'repo.fields.password', placeholderKey: 'repo.placeholder.password', required: true, secret: true },
    ],
  },
}

const DEFAULT_REPO_NAME = 'repo1'

const emptyConfig = (repoType: string) => {
  const meta = REPO_TYPES[repoType]
  if (!meta) return {}
  const base: Record<string, string> = {}
  meta.fields.forEach(field => { base[field.name] = '' })
  return base
}

const findField = (repoType: string, fieldName: string) => REPO_TYPES[repoType]?.fields.find(f => f.name === fieldName)

const formatDateTime = (value?: string | null) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

const truncateText = (value?: string | null, max = 120) => {
  if (!value) return ''
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

const summariseConfig = (repo: Repo, t: (key: string, params?: Record<string,string|number>) => string) => {
  const cfg = repo.config || {}
  const entries: string[] = []
  Object.entries(cfg).forEach(([key, value]) => {
    if (key === 'has_secrets') return
    const field = findField(repo.type, key)
    const label = t(field?.labelKey || `repo.fields.${key}`)
    entries.push(`${label}: ${String(value)}`)
  })
  if (cfg.has_secrets) entries.push(t('repo.summary.secrets'))
  return entries.join(' | ')
}

export default function Repos() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id!
  const { push } = useToasts()
  const { has } = usePerms()
  const [items, setItems] = useState<Repo[]>([])
  const [name, setName] = useState(DEFAULT_REPO_NAME)
  const [type, setType] = useState<keyof typeof REPO_TYPES>('s3')
  const [configValues, setConfigValues] = useState<Record<string, string>>(emptyConfig('s3'))
  const [editingId, setEditingId] = useState<number | undefined>()
  const [originalConfigValues, setOriginalConfigValues] = useState<Record<string, string>>({})
  const [draftBackup, setDraftBackup] = useState<{ type: keyof typeof REPO_TYPES; name: string; config: Record<string, string> } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [confirmId, setConfirmId] = useState<number|undefined>()
  const [unquarantineId, setUnquarantineId] = useState<number|undefined>()
  const editing = typeof editingId === 'number'

  const changeType = (nextType: keyof typeof REPO_TYPES, options?: { reset?: boolean }) => {
    setType(nextType)
    if (options?.reset === false) return
    setConfigValues(emptyConfig(nextType))
  }

  const restoreDraft = () => {
    if (draftBackup) {
      changeType(draftBackup.type, { reset: false })
      setConfigValues({ ...draftBackup.config })
      setName(draftBackup.name)
    } else {
      changeType('s3')
      setName(DEFAULT_REPO_NAME)
    }
    setDraftBackup(null)
  }

  const startEdit = (repo: Repo) => {
    if (!editing) {
      setDraftBackup({ type, name, config: { ...configValues } })
    }
    const repoType = repo.type as keyof typeof REPO_TYPES
    changeType(repoType, { reset: false })
    const metaDef = REPO_TYPES[repoType]
    const cfg = { ...(repo.config || {}) }
    delete (cfg as any).has_secrets
    const initialValues = emptyConfig(repoType)
    const originalValues: Record<string, string> = {}
    if (metaDef) {
      metaDef.fields.forEach(field => {
        if (field.secret) {
          originalValues[field.name] = ''
          return
        }
        const raw = cfg[field.name]
        const strValue = raw === undefined || raw === null ? '' : String(raw)
        initialValues[field.name] = strValue
        originalValues[field.name] = strValue
      })
    }
    setConfigValues(initialValues)
    setOriginalConfigValues(originalValues)
    setName(repo.name)
    setEditingId(repo.id)
  }

  const cancelEdit = () => {
    setEditingId(undefined)
    setOriginalConfigValues({})
    restoreDraft()
  }

  const formatPreflightStatus = (repo: Repo) => {
    if (!repo.last_preflight_status) return t('preflight.status.never') || 'Never run'
    const timestamp = repo.last_preflight_at ? formatDateTime(repo.last_preflight_at) : ''
    return `${repo.last_preflight_status} ${timestamp}`.trim()
  }

  const runRepoPreflight = async (repo: Repo) => {
    try {
      await apiPost(`/repositories/${repo.id}/preflight`, { reason: 'manual' })
      push({ text: t('preflight.feedback.queued') || 'Preflight queued', kind: 'success' })
      await load()
    } catch (e: any) {
      push({ text: e?.message || t('preflight.errors.failed') || 'Preflight failed', kind: 'error' })
    }
  }

  const update = async () => {
    if (!editingId) return
    const meta = REPO_TYPES[type]
    if (!meta) { push({ text: t('repo.errors.unsupported'), kind: 'error' }); return }
    const trimmedName = name.trim()
    if (!trimmedName) { push({ text: t('repo.errors.name_required'), kind: 'error' }); return }
    const missing = meta.fields.filter(field => field.required && !((configValues[field.name] || '').trim()) && !(field.secret && editing))
    if (missing.length) {
      const missingLabels = missing.map(field => t(field.labelKey)).join(', ')
      push({ text: t('repo.errors.missing_fields', { fields: missingLabels }), kind: 'error' })
      return
    }
    const payload: Record<string, string> = {}
    meta.fields.forEach(field => {
      const raw = (configValues[field.name] ?? '') as string
      const trimmed = raw.trim()
      if (trimmed) {
        payload[field.name] = trimmed
        return
      }
      if (!field.secret) {
        const previous = originalConfigValues[field.name] || ''
        if (previous) payload[field.name] = ''
      }
    })
    const body: { name: string; config?: Record<string, string> } = { name: trimmedName }
    if (Object.keys(payload).length > 0) {
      body.config = payload
    }
    try {
      await apiPut(`/repositories/${editingId}`, body)
      push({ text: t('repo.feedback.updated'), kind: 'success' })
      setName(trimmedName)
      await load()
      setEditingId(undefined)
      setOriginalConfigValues({})
      restoreDraft()
    } catch (e: any) {
      push({ text: e?.message || t('repo.errors.update_failed'), kind: 'error' })
    }
  }


  const load = async () => {
    setLoading(true)
    try {
      if (tenantId) {
        const data = await apiGet(`/tenants/${tenantId}/repositories`)
        setItems(data)
        setError(undefined)
      }
    }
    catch (e: any) { setError(e?.message || t('repo.errors.load_failed')) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [tenantId])

  const meta = useMemo(() => REPO_TYPES[type], [type])
  const quarantinedRepos = useMemo(() => items.filter(r => r.quarantined), [items])

  const create = async () => {
    if (!meta) { push({ text: t('repo.errors.unsupported'), kind: 'error' }); return }
    const trimmedName = name.trim()
    if (!trimmedName) { push({ text: t('repo.errors.name_required'), kind: 'error' }); return }
    const missing = meta.fields.filter(f => f.required && !(configValues[f.name] || '').trim())
    if (missing.length) {
      const missingLabels = missing.map(f => t(f.labelKey)).join(', ')
      push({ text: t('repo.errors.missing_fields', { fields: missingLabels }), kind: 'error' })
      return
    }
    const validationErrors: string[] = []
    if (type === 'sftp') {
      const host = (configValues.host || '').trim()
      if (host && !/^[A-Za-z0-9._-]+$/.test(host)) validationErrors.push(t('repo.errors.sftp_host'))
      const port = (configValues.port || '').trim()
      if (port && (!/^\d+$/.test(port) || Number(port) < 1 || Number(port) > 65535)) validationErrors.push(t('repo.errors.sftp_port'))
      const path = (configValues.path || '').trim()
      if (path && !path.startsWith('/')) validationErrors.push(t('repo.errors.sftp_path'))
    } else if (type === 'rest') {
      const url = (configValues.url || '').trim()
      try {
        const parsed = new URL(url)
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error()
      } catch {
        validationErrors.push(t('repo.errors.rest_url'))
      }
    } else if (type === 'swift') {
      const url = (configValues.auth_url || '').trim()
      try {
        const parsed = new URL(url)
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error()
      } catch {
        validationErrors.push(t('repo.errors.swift_auth_url'))
      }
    } else if (type === 'gs') {
      const jsonText = (configValues.credentials_json || '').trim()
      if (jsonText) {
        try { JSON.parse(jsonText) } catch { validationErrors.push(t('repo.errors.gs_credentials')) }
      }
    } else if (type === 'rclone') {
      const remote = (configValues.remote || '').trim()
      if (!remote) validationErrors.push(t('repo.errors.rclone_remote'))
    }
    if (validationErrors.length) {
      validationErrors.forEach(msg => push({ text: msg, kind: 'error' }))
      return
    }
    const payload: Record<string, string> = {}
    meta.fields.forEach(field => {
      const raw = (configValues[field.name] || '').trim()
      if (raw) payload[field.name] = raw
    })
    try {
      await apiPost(`/tenants/${tenantId}/repositories`, { name: trimmedName, type, config: payload })
      setName(DEFAULT_REPO_NAME)
      setConfigValues(emptyConfig(type))
      await load()
      push({ text: t('repo.feedback.created'), kind: 'success' })
    } catch (e: any) {
      push({ text: e?.message || t('repo.errors.create_failed'), kind: 'error' })
    }
  }

  const unquarantine = async (repoId: number) => {
    try {
      await apiPost(`/repositories/${repoId}/unquarantine`, {})
      await load()
      push({ text: t('repo.feedback.unquarantined'), kind: 'success' })
    } catch (e: any) {
      push({ text: e?.message || t('repo.errors.unquarantine_failed'), kind: 'error' })
    }
  }

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t('titles.repos')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('help.repos')}</p>
      {editing && (
        <div className="mb-3 rounded border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-500 dark:bg-blue-500/10 dark:text-blue-200">{t('repo.feedback.edit_mode', { name })}</div>
      )}
      {quarantinedRepos.length > 0 && (
        <div className="mb-3 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-500 dark:bg-amber-500/10 dark:text-amber-200">{t('repo.hint.quarantined', { count: quarantinedRepos.length })}</div>
      )}
      <div className="grid gap-4">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('table.type')}</span>
            <select className="input" value={type} onChange={e => changeType(e.target.value as keyof typeof REPO_TYPES)} disabled={!has('repos.write') || editing}>
              {Object.entries(REPO_TYPES).map(([value, cfg]) => (
                <option key={value} value={value}>{t(cfg.labelKey)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('table.name')}</span>
            <input className="input" placeholder={t('ph.name')} value={name} onChange={e => setName(e.target.value)} disabled={!has('repos.write')} />
          </label>
          {!editing ? (
            <button className="btn h-11" onClick={create} disabled={!has('repos.write')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6Z"/></svg>
              {t('buttons.create')}
            </button>
          ) : (
            <div className="flex gap-2">
              <button className="btn h-11" onClick={update} disabled={!has('repos.write')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M5 13l4 4L19 7l-1.4-1.4L9 14.2 6.4 11.6 5 13Z"/></svg>
                {t('buttons.save')}
              </button>
              <button className="btn-secondary h-11" onClick={cancelEdit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
                {t('buttons.cancel')}
              </button>
            </div>
          )}
        </div>
        {meta && (
          <div className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t(meta.descriptionKey)}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {meta.fields.map(field => {
                const value = configValues[field.name] || ''
                const placeholder = field.placeholderKey ? t(field.placeholderKey) : ''
                const disabled = !has('repos.write')
                const helperParts: string[] = []
                if (field.helperKey) helperParts.push(t(field.helperKey))
                if (editing && field.secret) helperParts.push(t('repo.helper.secret_keep'))
                const helper = helperParts.join('\n')
                const onChange = (e: any) => setConfigValues(prev => ({ ...prev, [field.name]: e.target.value }))
                return (
                  <label key={field.name} className="flex flex-col text-sm">
                    <span className="mb-1 font-medium">
                      {t(field.labelKey)}{field.required && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="input min-h-[120px]"
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        rows={6}
                      />
                    ) : (
                      <input
                        className="input"
                        type={field.secret ? 'password' : 'text'}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                      />
                    )}
                    {helper && <span className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{helper}</span>}
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {!has('repos.write') && <p className="text-xs text-gray-500 mt-2">{t('repo.feedback.no_permission')}</p>}
      <div className="mt-6">
        {loading ? <p>{t('common.loading')}</p> : error ? <p className="text-red-600">{error}</p> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                <th className="text-left">{t('table.name')}</th>
                <th>{t('table.type')}</th>
                <th>{t('repo.table.status')}</th>
                <th>{t('repo.table.preflight') || 'Preflight'}</th>
                <th className="text-left">{t('repo.table.last_error')}</th>
                <th className="text-left">{t('repo.table.config')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => {
                const statusClass = r.quarantined
                  ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'
                const statusLabel = r.quarantined ? t('repo.status.quarantined') : t('repo.status.healthy')
                const lastError = truncateText(r.last_error)
                const isEditingRow = editing && editingId === r.id
                return (
                  <tr key={r.id} className={`border-t border-gray-200 dark:border-gray-700 ${isEditingRow ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    <td>{r.name}</td>
                    <td className="text-center">{t(REPO_TYPES[r.type]?.labelKey || r.type)}</td>
                <td className="text-center">
                  <span className={`inline-block rounded px-2 py-1 text-xs font-medium ${statusClass}`}>{statusLabel}</span>
                  {r.last_error_at && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{formatDateTime(r.last_error_at)}</div>
                  )}
                </td>
                <td>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">{formatPreflightStatus(r)}</span>
                    {has('repos.write') && (
                      <button className="link" onClick={() => runRepoPreflight(r)}>{t('preflight.actions.run') || 'Run preflight'}</button>
                    )}
                  </div>
                </td>
                <td className="text-left text-xs text-gray-700 dark:text-gray-300">{lastError || '-'}</td>
                    <td>{summariseConfig(r, t) || '-'}</td>
                    <td className="text-right">
                      {has('repos.write') && (
                        <div className="flex justify-end gap-2">
                          <button className="btn-secondary" onClick={() => startEdit(r)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M5 19h3l10.1-10.1-3-3L5 16v3Zm12.7-12.7 1.3-1.3-3-3-1.3 1.3 3 3Z"/></svg>
                            {t('buttons.edit')}
                          </button>
                          {r.quarantined && (
                            <button className="btn-secondary" onClick={() => setUnquarantineId(r.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M5 13l4 4L19 7l-1.4-1.4L9 14.2l-2.6-2.6L5 13Z"/></svg>
                              {t('repo.actions.unquarantine')}
                            </button>
                          )}
                          <button className="btn-secondary" onClick={() => setConfirmId(r.id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M7 6h10l-1 14H8L7 6Zm2-2h6l1 2H8l1-2Z"/></svg>
                            {t('buttons.delete')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      <Confirm open={!!confirmId} onClose={() => setConfirmId(undefined)} onConfirm={async () => {
        if (!confirmId) return
        try {
          await fetch(`/repositories/${confirmId}`, { method: 'DELETE', credentials: 'include' })
          await load()
        } finally {
          setConfirmId(undefined)
        }
      }}>{t('repo.feedback.confirm_delete')}</Confirm>
      <Confirm open={!!unquarantineId} onClose={() => setUnquarantineId(undefined)} onConfirm={async () => {
        if (!unquarantineId) return
        try {
          await unquarantine(unquarantineId)
        } finally {
          setUnquarantineId(undefined)
        }
      }}>{t('repo.feedback.confirm_unquarantine')}</Confirm>
    </div>
  )
}
